import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
    withStyles,
} from '@material-ui/core';
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router';
import { axiosInstance, cancelToken } from '../../utils/axiosInstance';
import {
    fwTaskCreationCopy as copy,
    endpoint,
    fwTaskType,
    fwTaskTypeLabels,
    snackbarDefaultValue,
    snackbarVariant,
} from '../../utils/constants';
import { findDuplicateSerials, parseSerialList } from '../../utils/fwHelpers';
import { probeWorkflow, refusalText, workflowModes } from '../../utils/fwWorkflow';
import Snackbar from '../Snackbar/Snackbar';
import TitleHeader from '../TitleHeader/TitleHeader';
import FwWorkflowTaskForm from './FwWorkflowTaskForm';

const DUPLICATE_LIST_LIMIT = 10;

const APN_LIST_LIMIT = 200;

const AUTOMATIC_APN = '__automatic__';

const summariseSerials = serials => {
    const visible = serials.slice(0, DUPLICATE_LIST_LIMIT);
    const hidden = serials.length - visible.length;

    return hidden > 0 ? `${visible.join(', ')} i ${hidden} więcej` : visible.join(', ');
};

const styles = theme => ({
    root: {
        margin: '1rem 2rem',
    },
    form: {
        display: 'flex',
        flexDirection: 'column',
        gap: '1.5rem',
        maxWidth: '40rem',
    },
    row: {
        display: 'flex',
        gap: '1.5rem',
        alignItems: 'center',
    },
    actions: {
        display: 'flex',
        gap: '1rem',
    },
    fileInput: {
        display: 'none',
    },
    fileButton: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: '6px 16px',
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        borderRadius: theme.shape.borderRadius,
        cursor: 'pointer',
        fontFamily: theme.typography.fontFamily,
        fontSize: theme.typography.button.fontSize,
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
    },
    fileButtonIcon: {
        marginRight: '0.5rem',
    },
    conflictText: {
        marginBottom: '1rem',
    },
});

const LegacyTaskForm = ({ classes }) => {
    const history = useHistory();
    const [name, setName] = useState('');
    const [files, setFiles] = useState([]);
    const [fwFileId, setFwFileId] = useState('');
    const [type, setType] = useState(fwTaskType.PRONAQUA);
    const [apns, setApns] = useState([]);
    const [apn, setApn] = useState(AUTOMATIC_APN);
    const [serialsText, setSerialsText] = useState('');
    const [restrictionType, setRestrictionType] = useState('none');
    const [restrictionsText, setRestrictionsText] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [conflictData, setConflictData] = useState(null);
    const [snackbarData, setSnackbarData] = useState(snackbarDefaultValue);

    const showMessage = (message, variant) => {
        setSnackbarData({ isSnackbarOpen: true, message, variant, errorInfo: '' });
    };

    const handleIsSnackbarOpen = () => setSnackbarData(snackbarDefaultValue);

    useEffect(() => {
        axiosInstance
            .get(endpoint.FW_FILES)
            .then(res => setFiles(res.data?.data ?? res.data ?? []))
            .catch(() => showMessage('Błąd pobierania listy plików', snackbarVariant.ERROR));

        axiosInstance
            .get(endpoint.FW_APNS, { params: { per_page: APN_LIST_LIMIT } })
            .then(res => setApns(res.data?.data ?? res.data ?? []))
            .catch(() => showMessage('Błąd pobierania listy APN', snackbarVariant.ERROR));
    }, []);

    const handleCsvUpload = event => {
        const selected = event.target.files[0];
        if (!selected) return;
        const reader = new FileReader();
        reader.onload = () => {
            const existing = parseSerialList(serialsText);
            const imported = parseSerialList(reader.result);
            setSerialsText([...existing, ...imported].join('\n'));
        };
        reader.readAsText(selected);
        event.target.value = '';
    };

    const handleSubmit = () => {
        if (!name || !fwFileId) {
            showMessage('Podaj nazwę zadania oraz wybierz plik firmware', snackbarVariant.ERROR);
            return;
        }

        const serials = parseSerialList(serialsText);
        const duplicates = findDuplicateSerials(serials);
        if (duplicates.length) {
            showMessage(
                `Zduplikowane numery: ${summariseSerials(duplicates)}. Usuń duplikaty i spróbuj ponownie.`,
                snackbarVariant.ERROR
            );
            return;
        }

        setIsSubmitting(true);
        const payload = {
            name,
            fw_file_id: Number(fwFileId),
            firmware_type: type,
            serial_numbers: serials,
        };
        if (apn !== AUTOMATIC_APN) {
            payload.apn = apn;
        }
        if (restrictionType !== 'none') {
            payload.revision_restriction_type = restrictionType;
            payload.revision_restrictions = parseSerialList(restrictionsText).map(Number);
        }

        axiosInstance
            .post(endpoint.FW_TASKS, payload)
            .then(res => {
                showMessage('Zadanie utworzone', snackbarVariant.SUCCESS);
                history.push(`/fw-task/${res.data.id}`);
            })
            .catch(err => {
                const data = err.response?.data;

                if (data?.conflicts?.length) {
                    setConflictData({ message: data.message, conflicts: data.conflicts });

                    return;
                }

                showMessage(data?.message || 'Błąd tworzenia zadania', snackbarVariant.ERROR);
            })
            .finally(() => setIsSubmitting(false));
    };

    return (
        <div className={classes.root}>
            <TitleHeader title="Moduł FW — Nowe zadanie" />
            <form className={classes.form} noValidate autoComplete="off">
                <TextField
                    label="Nazwa zadania"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    variant="outlined"
                    required
                />
                <FormControl variant="outlined">
                    <InputLabel>Plik firmware</InputLabel>
                    <Select
                        value={fwFileId}
                        onChange={e => setFwFileId(e.target.value)}
                        label="Plik .bin">
                        {files.length === 0 ? (
                            <MenuItem value="" disabled>
                                Brak plików — dodaj plik w sekcji Obsługa plików .bin
                            </MenuItem>
                        ) : (
                            files.map(f => (
                                <MenuItem key={f.id} value={f.id}>
                                    {f.filename}
                                </MenuItem>
                            ))
                        )}
                    </Select>
                </FormControl>
                <Typography variant="caption" color="textSecondary">
                    Pliki można dodać w sekcji Moduł FW — Obsługa plików .bin.
                </Typography>
                <FormControl component="fieldset">
                    <FormLabel component="legend">Typ urządzenia</FormLabel>
                    <RadioGroup value={type} onChange={e => setType(Number(e.target.value))} row>
                        {Object.entries(fwTaskType).map(([key, value]) => (
                            <FormControlLabel
                                key={key}
                                value={value}
                                control={<Radio color="primary" />}
                                label={fwTaskTypeLabels[value]}
                            />
                        ))}
                    </RadioGroup>
                </FormControl>
                <FormControl variant="outlined">
                    <InputLabel>APN (opcjonalnie)</InputLabel>
                    <Select value={apn} onChange={e => setApn(e.target.value)} label="APN (opcjonalnie)">
                        <MenuItem value={AUTOMATIC_APN}>Automatycznie z Unikatów</MenuItem>
                        {apns.map(item => (
                            <MenuItem key={item.id} value={item.apn}>
                                {item.apn}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                <Typography variant="caption" color="textSecondary">
                    Wybrany APN ustawia adres połączenia dla wszystkich urządzeń zadania. Bez wyboru adres
                    pochodzi z numeru seryjnego każdego urządzenia.
                </Typography>
                <TextField
                    label="Numery seryjne (jeden na linię lub po przecinku)"
                    value={serialsText}
                    onChange={e => setSerialsText(e.target.value)}
                    variant="outlined"
                    multiline
                    minRows={6}
                />
                <div className={classes.row}>
                    <label className={classes.fileButton}>
                        <input
                            type="file"
                            accept=".csv,.txt"
                            onChange={handleCsvUpload}
                            className={classes.fileInput}
                        />
                        <CloudUploadIcon className={classes.fileButtonIcon} />
                        Wczytaj CSV
                    </label>
                </div>
                <FormControl component="fieldset">
                    <FormLabel component="legend">Restrykcje rewizji</FormLabel>
                    <RadioGroup
                        value={restrictionType}
                        onChange={e => setRestrictionType(e.target.value)}
                        row>
                        <FormControlLabel value="none" control={<Radio color="primary" />} label="Brak" />
                        <FormControlLabel
                            value="allow"
                            control={<Radio color="primary" />}
                            label="Tylko urządzenia z rewizją"
                        />
                        <FormControlLabel
                            value="deny"
                            control={<Radio color="primary" />}
                            label="Tylko urządzenia BEZ rewizji"
                        />
                    </RadioGroup>
                </FormControl>
                {restrictionType !== 'none' && (
                    <TextField
                        label="Lista rewizji (np. 1000, 1001, 1002)"
                        value={restrictionsText}
                        onChange={e => setRestrictionsText(e.target.value)}
                        variant="outlined"
                    />
                )}
                <div className={classes.actions}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleSubmit}
                        disabled={isSubmitting}>
                        Utwórz zadanie
                    </Button>
                </div>
            </form>
            <Dialog
                open={Boolean(conflictData)}
                onClose={() => setConflictData(null)}
                fullWidth
                maxWidth="sm">
                <DialogTitle>Zadanie nie zostało utworzone</DialogTitle>
                <DialogContent dividers>
                    <Typography className={classes.conflictText}>{conflictData?.message}</Typography>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Numer</TableCell>
                                <TableCell>Zadanie</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {(conflictData?.conflicts ?? []).map(conflict => (
                                <TableRow key={`${conflict.serial_number}-${conflict.task_id}`}>
                                    <TableCell>{conflict.serial_number}</TableCell>
                                    <TableCell>#{conflict.task_id}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </DialogContent>
                <DialogActions>
                    <Button color="primary" variant="contained" onClick={() => setConflictData(null)}>
                        Zamknij
                    </Button>
                </DialogActions>
            </Dialog>
            <Snackbar snackbarData={snackbarData} handleIsSnackbarOpen={handleIsSnackbarOpen} />
        </div>
    );
};

const StyledLegacyTaskForm = withStyles(styles)(LegacyTaskForm);

const FwTaskForm = () => {
    const [mode, setMode] = useState(workflowModes.LOADING);
    const [error, setError] = useState(null);
    const lifetime = useRef({ active: false, generation: 0 });
    const source = useRef(null);
    const load = async () => {
        const generation = ++lifetime.current.generation;
        source.current?.cancel();
        source.current = cancelToken.source();
        setMode(workflowModes.LOADING);
        setError(null);
        const result = await probeWorkflow({ cancelToken: source.current.token });
        if (!lifetime.current.active || generation !== lifetime.current.generation) return;
        setMode(result.mode);
        if (result.mode === workflowModes.ERROR) setError(refusalText(result.refusal, copy.probeError));
    };
    useEffect(() => {
        lifetime.current.active = true;
        load();
        return () => {
            lifetime.current.active = false;
            lifetime.current.generation += 1;
            source.current?.cancel();
        };
    }, []);
    if (mode === workflowModes.CONFIGURED) return <FwWorkflowTaskForm />;
    if (mode === workflowModes.DISABLED) return <StyledLegacyTaskForm />;
    return (
        <div>
            <TitleHeader title={copy.title} />
            {mode === workflowModes.LOADING ? (
                <Typography role="status">{copy.loading}</Typography>
            ) : (
                <>
                    <Typography role="alert">{error || copy.probeError}</Typography>
                    <Button onClick={load}>{copy.reload}</Button>
                </>
            )}
        </div>
    );
};

export default FwTaskForm;
